import { API_BASE_URL } from "../config";

/**
 * Generates a secure random state token for CSRF protection (client-side)
 */
function generateStateToken(): string {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return btoa(String.fromCharCode(...array))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=/g, '');
}

/**
 * Server action to get LinkedIn OAuth authorization URL with security enhancements:
 * - Uses environment variables for sensitive credentials
 * - Implements CSRF protection with state parameter
 * - Stores state in httpOnly cookie for validation
 */
export async function getLinkedInAuthUrl(): Promise<string> {
	// Get credentials from environment variables (use import.meta.env in Vite)
	const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID || "86jne8123ya76z";
	const redirectUri =
		import.meta.env.VITE_LINKEDIN_REDIRECT_URI ||
		"https://api.profound.club/linkedin/auth/callback";
	const scope =
		import.meta.env.VITE_LINKEDIN_SCOPE || "openid email profile r_basicprofile";

	if (!clientId) {
		throw new Error("LINKEDIN_CLIENT_ID environment variable is not set");
	}

	// Generate and store state token for CSRF protection
	const stateToken = generateStateToken();
	
	// Store state in sessionStorage (expires when tab closes, or manually after 10 minutes)
	if (typeof window !== 'undefined') {
		sessionStorage.setItem("linkedin_oauth_state", stateToken);
		// Set expiration timestamp (10 minutes)
		sessionStorage.setItem("linkedin_oauth_state_expires", String(Date.now() + 600000));
	}

	// Build OAuth URL with state parameter
	const params = new URLSearchParams({
		response_type: "code",
		client_id: clientId,
		redirect_uri: redirectUri,
		scope: scope,
		state: stateToken, // CSRF protection
	});

	return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
}

/**
 * Validates the state token from OAuth callback
 * This should be called in your callback handler
 */
export async function validateOAuthState(state: string): Promise<boolean> {
	if (typeof window === 'undefined') {
		return false;
	}

	const storedState = sessionStorage.getItem("linkedin_oauth_state");
	const expires = sessionStorage.getItem("linkedin_oauth_state_expires");

	// Check if expired
	if (expires && Date.now() > parseInt(expires)) {
		sessionStorage.removeItem("linkedin_oauth_state");
		sessionStorage.removeItem("linkedin_oauth_state_expires");
		return false;
	}

	if (!storedState || storedState !== state) {
		return false;
	}

	// Delete the state after validation (one-time use)
	sessionStorage.removeItem("linkedin_oauth_state");
	sessionStorage.removeItem("linkedin_oauth_state_expires");
	return true;
}

/**
 * Server Action: Stores authentication tokens in secure HTTP-only cookies
 * 
 * NOTE: This is a Server Action and can only be called from:
 * - Client components (as a server action)
 * - Route Handlers
 * - Other Server Actions
 * 
 * For OAuth callbacks, tokens are processed via /api/auth/callback Route Handler
 * which handles cookies directly. This function is kept for programmatic use cases.
 */
export async function storeAuthTokens(
	accessToken: string,
	refreshToken?: string,
): Promise<void> {
	if (typeof window === 'undefined') {
		return;
	}

	// Store access token in localStorage
	localStorage.setItem("accessToken", accessToken);

	// Store refresh token if provided
	if (refreshToken) {
		localStorage.setItem("refreshToken", refreshToken);
	}
}

/**
 * Server-side version - always returns false since tokens are in localStorage
 * Use isAuthenticatedClient() from lib/utils/auth.ts in client components instead
 */
export async function isAuthenticated(): Promise<boolean> {
	// Since tokens are now in localStorage (client-side only),
	// server-side can't check authentication
	return false;
}

/**
 * Server-side version - always returns null since tokens are in localStorage
 * Use getAccessTokenClient() from lib/utils/auth.ts in client components instead
 */
export async function getAccessToken(): Promise<string | null> {
	// Since tokens are now in localStorage (client-side only),
	// server-side can't access them
	return null;
}

/**
 * Server-side version - always returns null since tokens are in localStorage
 * Use getRefreshTokenClient() from lib/utils/auth.ts in client components instead
 */
export async function getRefreshToken(): Promise<string | null> {
	// Since tokens are now in localStorage (client-side only),
	// server-side can't access them
	return null;
}

/**
 * Server-side version - no-op since tokens are in localStorage
 * Use clearAuthTokensClient() from lib/utils/auth.ts in client components instead
 */
export async function clearAuthTokens(): Promise<void> {
	// Since tokens are now in localStorage (client-side only),
	// server-side can't clear them
}

/**
 * Server-side function to fetch LinkedIn user profile
 * Accepts an optional access token parameter for server-side fetching
 * If no token is provided, returns null (client-side will handle it)
 */
export async function fetchLinkedInProfile(
	detailed: boolean = true,
	accessToken?: string | null,
): Promise<any | null> {
	// If no token provided, return null (client-side will handle it)
	if (!accessToken) {
		return null;
	}

	try {
		const response = await fetch(
			`${API_BASE_URL}/protected/user-profile?detailed=${detailed}`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"Content-Type": "application/json",
					Accept: "*/*",
					"Accept-Language": "en-US,en;q=0.9",
				},
				cache: "no-store",
			},
		);

		if (!response.ok) {
			if (response.status === 401) {
				return null;
			}
			return null;
		}

		const data = await response.json();

		if (data.success && data.data) {
			return data.data;
		}

		return null;
	} catch (error) {
		console.error("Failed to fetch LinkedIn profile:", error);
		return null;
	}
}

/**
 * Server action to check onboarding status and determine if redirect is needed
 */
export async function checkOnboardingAndRedirect(
	linkedinUserData: any | null,
): Promise<{ shouldRedirect: boolean; redirectTo?: string }> {
	if (!linkedinUserData) {
		return { shouldRedirect: false };
	}

	// Check if onboarding is complete
	const isOnboardingComplete = 
		linkedinUserData.onboarding_complete === true ||
		linkedinUserData.onboarding_status === "complete" ||
		(linkedinUserData.user && linkedinUserData.user.onboarding_complete === true);

	if (isOnboardingComplete) {
		return { shouldRedirect: true, redirectTo: "/profile" };
	}

	return { shouldRedirect: false };
}

/**
 * Server-side function to check if profile data exists
 * Accepts an optional access token parameter for server-side fetching
 * Returns true if profile data exists, false otherwise
 */
export async function checkProfileDataExists(
	accessToken?: string | null,
): Promise<boolean> {
	// If no token provided, return false (client-side will handle it)
	if (!accessToken) {
		return false;
	}

	try {
		const response = await fetch(
			`${API_BASE_URL}/protected/profiling/data`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"Content-Type": "application/json",
					Accept: "*/*",
					"Accept-Language": "en-US,en;q=0.9",
				},
				cache: "no-store",
			},
		);

		if (!response.ok) {
			if (response.status === 401 || response.status === 404) {
				return false;
			}
			return false;
		}

		const data = await response.json();

		// Check if profile data exists and is valid
		if (data.success && data.data) {
			return true;
		}

		return false;
	} catch (error) {
		console.error("Failed to check profile data:", error);
		return false;
	}
}

/**
 * Server-side function to check if user is authenticated via whoami endpoint
 * Accepts an optional access token parameter for server-side fetching
 * Returns true if user is authenticated, false otherwise
 */
export async function checkUserAuthenticated(
	accessToken?: string | null,
): Promise<boolean> {
	// If no token provided, return false (client-side will handle it)
	if (!accessToken) {
		return false;
	}

	try {
		const response = await fetch(
			`${API_BASE_URL}/protected/whoami`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"Content-Type": "application/json",
					Accept: "*/*",
					"Accept-Language": "en-US,en;q=0.9",
				},
				cache: "no-store",
			},
		);

		if (!response.ok) {
			if (response.status === 401 || response.status === 404) {
				return false;
			}
			return false;
		}

		const data = await response.json();

		// Check if user data exists (user is authenticated)
		if (data.success && data.data) {
			return true;
		}

		return false;
	} catch (error) {
		console.error("Failed to check user authentication:", error);
		return false;
	}
}
